import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ShieldCheck, ArrowRight, Sparkles, User, Award, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { captureTrialSourceFromURL, getTrialSource, clearTrialSource } from "@/lib/trialSource";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { EmployerSubscriptionSection } from "@/components/pricing/EmployerSubscriptionSection";
import { TrialConsentDialog } from "@/components/billing/TrialConsentDialog";

// iter-156 — Talent Pro + Employer prices revealed. Starter / Growth
// trial is enforced server-side (see /api/billing/checkout). The admin
// draft preview still overrides these when ?employerPreview is supplied.
const CONTACT_PRICE = "Contact us";

const seekerPlans = [
    {
        name: "Free",
        planKey: null,
        price: "$0",
        period: "forever",
        desc: "For every professional.",
        features: [
            "Talent Passport™",
            "Resume Upload",
            "AI Resume Optimizer (3 uses / mo)",
            "AI Job Matching (3 uses / mo)",
            "Basic Cover Letter Generator",
            "Basic Interview Practice",
            "Application Tracker",
            "Identity verification — available on employer request",
            "Verified Talent Passport™ Badge",
            "Employer Visibility",
        ],
        cta: "Get started free",
        to: "/talent",
        testId: "pricing-seeker-free",
    },
    {
        name: "Talent Pro",
        planKey: "talent_pro_month",
        annualPlanKey: "talent_pro_year",
        price: "$9.99",
        period: "/month",
        annualPrice: "$89",
        annualPeriod: "/year — save ~26%",
        badge: "Unlock every AI tool",
        highlight: true,
        desc: "Everything in Free, plus:",
        features: [
            "Unlimited Resume Tailoring",
            "Unlimited AI Resume Optimization",
            "Unlimited Cover Letters",
            "Unlimited AI Interview Coaching",
            "Salary Negotiation Coach",
            "Priority Employer Visibility",
            "Advanced AI Career Insights",
            "Priority Support",
        ],
        cta: "Upgrade to Pro",
        testId: "pricing-seeker-pro",
    },
];

const PlanCard = ({ plan, ctaHref, onCheckout, stripeReady, checkoutBusy }) => {
    const [annual, setAnnual] = useState(false);
    const hasAnnualToggle = Boolean(plan.annualPlanKey && plan.annualPrice);
    const activePlanKey = annual ? plan.annualPlanKey : plan.planKey;
    const priceLabel = hasAnnualToggle && annual ? plan.annualPrice : plan.price;
    const periodLabel = hasAnnualToggle && annual ? plan.annualPeriod : plan.period;

    const handleClick = (e) => {
        if (!activePlanKey) return;  // Free / enterprise plans keep <Link> behavior
        e.preventDefault();
        onCheckout(activePlanKey);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            data-testid={plan.testId}
            className={`card-soft p-6 flex flex-col ${plan.highlight ? "ring-2 ring-emerald-500/60 shadow-lg" : ""}`}
        >
            <div className="flex items-center gap-2 mb-1">
                <h3 className="font-heading font-semibold text-lg text-foreground">{plan.name}</h3>
                {plan.badge && (
                    <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${plan.highlight ? "bg-emerald-500 text-white" : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30"}`}>
                        {plan.badge}
                    </span>
                )}
            </div>
            <p className="text-xs text-muted-foreground mb-4">{plan.desc}</p>
            {hasAnnualToggle && (
                <div className="inline-flex rounded-full bg-muted/60 p-1 text-[11px] font-semibold mb-4" data-testid={`${plan.testId}-cycle-toggle`}>
                    <button
                        type="button"
                        onClick={() => setAnnual(false)}
                        className={`px-3 py-1 rounded-full transition-colors ${!annual ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}
                        data-testid={`${plan.testId}-cycle-monthly`}
                    >Monthly</button>
                    <button
                        type="button"
                        onClick={() => setAnnual(true)}
                        className={`px-3 py-1 rounded-full transition-colors ${annual ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}
                        data-testid={`${plan.testId}-cycle-annual`}
                    >Annual · save 26%</button>
                </div>
            )}
            <div className="flex items-baseline gap-1 mb-5 flex-wrap">
                <span className={`font-heading font-bold text-foreground ${priceLabel === CONTACT_PRICE ? "text-sm" : "text-3xl"}`}>{priceLabel}</span>
                {periodLabel && <span className="text-xs text-muted-foreground">{periodLabel}</span>}
            </div>
            <ul className="space-y-2 text-sm text-foreground mb-6 flex-1">
                {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                        <Check size={13} className="mt-1 text-emerald-500 shrink-0" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>
            <Link
                to={plan.to || ctaHref || "/signup"}
                onClick={handleClick}
                className={`w-full inline-flex items-center justify-center gap-1 text-sm font-semibold rounded-full px-4 py-2.5 transition-colors ${plan.highlight ? "btn-green" : "btn-outline"} ${activePlanKey && !stripeReady ? "opacity-70" : ""}`}
                title={activePlanKey && !stripeReady ? "Stripe is not yet configured for this environment" : undefined}
            >
                {checkoutBusy && checkoutBusy === activePlanKey
                    ? <Loader2 size={14} className="animate-spin" />
                    : <>{plan.cta} <ArrowRight size={13} /></>}
            </Link>
        </motion.div>
    );
};

export default function Pricing({ employerPreview = null }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stripeStatus, setStripeStatus] = useState({ configured: false, available_plan_keys: [] });
    const [busy, setBusy] = useState(null);
    // iter-176 — trial-consent gate. Captures plan key when the user
    // clicks a trial CTA, pops the disclosure + unchecked consent
    // checkbox, and only redirects to Stripe after confirmation.
    const [consentPlan, setConsentPlan] = useState(null);
    const EMPLOYER_TRIAL_PLANS = new Set(["employer_starter_month", "employer_growth_month"]);

    useEffect(() => {
        captureTrialSourceFromURL();
        if (employerPreview) return;
        api.get("/stripe/status").then(({ data }) => setStripeStatus(data)).catch(() => { });
    }, [employerPreview]);

    const beginEmployerTrial = (planKey) => {
        // Spec: BEFORE the card form, show disclosure + unchecked consent.
        // Non-employer-trial checkouts (verifications, etc.) bypass this.
        if (!EMPLOYER_TRIAL_PLANS.has(planKey)) return proceedToCheckout(planKey);
        setConsentPlan(planKey);
    };

    const proceedToCheckout = async (planKey) => {
        if (!user) {
            // Push through signup first — user returns to /pricing after auth.
            navigate(`/signup?role=${planKey.startsWith("employer") ? "employer" : "professional"}&next=/pricing`);
            return;
        }
        if (!stripeStatus.configured) {
            toast.error("Billing isn't fully configured on this environment yet — please contact us and we'll activate your subscription manually.");
            return;
        }
        if (!stripeStatus.available_plan_keys.includes(planKey)) {
            toast.error("That plan isn't wired to Stripe yet — please contact us.");
            return;
        }
        try {
            setBusy(planKey);
            const source = getTrialSource();
            const { data } = await api.post("/billing/checkout", {
                kind: "subscription",
                plan_key: planKey,
                source,
            });
            // Clear the stashed source so a later checkout (e.g. upgrade
            // Starter → Growth from the dashboard) doesn't carry stale
            // "home_final_cta" attribution forward.
            clearTrialSource();
            window.location.assign(data.url);
        } catch (err) {
            toast.error(formatApiError(err));
            setBusy(null);
        }
    };

    const handleCheckout = beginEmployerTrial;

    return (
        <div className="bg-background" data-testid="pricing-page">
            {/* Hero */}
            <section className="pt-16 pb-8">
                <div className="mx-auto max-w-6xl px-6">
                    <div className="max-w-3xl">
                        <p className="overline text-emerald-600">The Verified Talent Platform™</p>
                        <h1 className="mt-2 font-heading font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl text-foreground">
                            Pricing for the AI-first way to hire.
                        </h1>
                        <p className="mt-4 text-base text-muted-foreground">
                            Two ways to hire: <b className="text-foreground">self-serve software</b> (Starter or Growth subscription — run the hire yourself with AI) or <b className="text-foreground">Simonara Recruit™</b> (1:1 with a real Simonara recruiter who sources, screens, and shortlists for a specific role). Pick one, or run both in parallel.
                        </p>
                    </div>
                </div>
            </section>

            {/* Employer plans */}
            <EmployerSubscriptionSection
                preview={employerPreview}
                onCheckout={handleCheckout}
                stripeReady={stripeStatus.configured}
                checkoutBusy={busy}
            />

            {/* Founding Employer banner */}
            <section className="pb-8">
                <div className="mx-auto max-w-6xl px-6">
                    <div className="rounded-3xl p-6 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent border border-emerald-500/30" data-testid="founding-employer-banner">
                        <div className="flex items-start justify-between gap-6 flex-wrap">
                            <div className="max-w-2xl">
                                <div className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white px-3 py-1 text-[10px] uppercase tracking-wider font-bold">
                                    <Award size={11} /> Founding Employer Network
                                </div>
                                <h3 className="mt-3 font-heading font-medium text-2xl text-foreground">
                                    First 10 employers get a permanent 12% recruiting rate.
                                </h3>
                                <p className="mt-2 text-sm text-muted-foreground">
                                    Founding Employers get a badge on their profile, featured placement on the site, and lock in a 12% <b className="text-foreground">Simonara Recruit™</b> placement fee — for every hire, forever.
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row items-stretch gap-2">
                                <Link to="/signup?role=employer" className="btn-green text-sm inline-flex items-center justify-center gap-1" data-testid="founding-cta">
                                    Claim a founding spot <ArrowRight size={13} />
                                </Link>
                                <Link to="/recruiting" className="btn-outline text-sm inline-flex items-center justify-center gap-1">
                                    Learn about Recruit™
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Talent plans */}
            <section className="pb-8">
                <div className="mx-auto max-w-6xl px-6">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
                        <div>
                            <div className="overline inline-flex items-center gap-1"><User size={11} /> For Talent</div>
                            <h2 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                                Free forever. Pro unlocks every AI tool.
                            </h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
                        {seekerPlans.map((p) => (
                            <PlanCard
                                key={p.name}
                                plan={p}
                                onCheckout={handleCheckout}
                                stripeReady={stripeStatus.configured}
                                checkoutBusy={busy}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Identity verification (v1 — Stripe Identity, employer request only, free to talent) */}
            <section className="pb-8">
                <div className="mx-auto max-w-6xl px-6">
                    <div className="card-soft p-6" data-testid="pricing-identity-verification">
                        <div className="overline inline-flex items-center gap-1"><ShieldCheck size={11} /> Identity verification</div>
                        <h3 className="mt-1 font-heading font-semibold text-xl text-foreground">Government-ID verification on any candidate</h3>
                        <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
                            Available on employer request — at no charge to talent. Employers see the verified outcome only; ID documents are never shared.
                        </p>
                    </div>
                </div>
            </section>

            {/* Recruit CTA */}
            <section className="pb-12">
                <div className="mx-auto max-w-6xl px-6">
                    <div className="rounded-3xl p-8 bg-brand-navy text-white flex items-start justify-between gap-6 flex-wrap" data-testid="recruit-cta">
                        <div className="max-w-2xl">
                            <div className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-[10px] uppercase tracking-wider font-bold">
                                <Sparkles size={11} /> 1:1 with a Simonara recruiter
                            </div>
                            <h3 className="mt-3 font-heading font-medium text-2xl">Prefer a real recruiter on your toughest role?</h3>
                            <p className="mt-2 text-sm text-white/80">
                                Simonara Recruit™ is a done-for-you alternative to the subscription — you book a 1:1 consultation with a Simonara recruiter who sources, screens, and shortlists candidates for a specific open role. You only interview finalists; we run the background check at the offer stage. Standard placement fee is <b>15%</b> of first-year salary — the <b>first 10 founding employers</b> lock in <b>12%</b> for every hire, forever.
                            </p>
                        </div>
                        <Link to="/recruiting?source=recruit_cta" className="inline-flex items-center gap-1 rounded-full bg-white text-brand-navy px-5 py-2.5 text-sm font-semibold hover:bg-white/90 transition-colors" data-testid="recruit-cta-link">
                            Book a 1:1 with Recruit™ <ArrowRight size={13} />
                        </Link>
                    </div>
                </div>
            </section>
            <TrialConsentDialog
                open={!!consentPlan}
                onOpenChange={(v) => { if (!v) setConsentPlan(null); }}
                planKey={consentPlan}
                planName={consentPlan === "employer_growth_month" ? "Growth" : consentPlan === "employer_starter_month" ? "Starter" : ""}
                busy={!!busy}
                onConfirm={() => {
                    const plan = consentPlan;
                    setConsentPlan(null);
                    if (plan) proceedToCheckout(plan);
                }}
            />
        </div>
    );
}
